import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch, Prop, Ref } from 'vue-property-decorator';
import { CLASSES_STORE } from '@/classes/store/classes.store';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import EdInputModel from '@/models/ed-input.model';
import Moment from 'moment';

@Component({
  name: 'ed-list-class-students'
})
export default class EdListClassStudentsComponent extends Vue {

  /* Props */
  @Prop() readonly classItem: any;
  @Prop() readonly orgId: number;

  /* Refs */
  @Ref('dataTable') readonly dataTable: any;
  
  /* Data */
  public search = EdInputModel('search',  '');
  public loading: boolean = true;
  public loadingAdd: boolean = false;
  public addObj: Object = {};
  public students: any = [];
  public studentsOrg: any = [];
  public inviteStudent: boolean = false;
  public pagination: object = {
    page: 1,
    itemsPerPage: 10
  }
  public paginationOrg: object = {
    current: 1,
    total: 0
  }
  public showPaginator: boolean = true;
  
  /* Computed */
  get  headers() { 
    return [
      { text:  this.$t('classes.headers.username'), align: 'start', value: this.inviteStudent ? 'username' : 'userId' },
      { text: this.$t('classes.headers.name'), value: 'firstName' },
      { text: this.$t('classes.headers.lastName'), value: 'lastName' },
      { text: this.$t('classes.headers.email'), value: 'email'  },
      { text: this.$t('classes.headers.actions'), value: 'actions', sortable: false }
    ]
  }

  get classStudents() {
    return this.$store.state.ClassesStore.classStudents;
  }

  get pagedOrganizationStudents() {
    return this.$store.state.ClassesStore.pagedOrganizationStudents;
  }

  get writeClassPriv() {
    return this.$store.state.AuthStore.privileges.WriteClass;
  }

  get isMainTeacher() {
    return this.$store.state.AuthStore.user.id === this.classItem.teacher.id
  }

  get isCoinstructor() {
    return this.$store.state.ClassesStore.classTeachers.findIndex(el => el.person.id === this.$store.state.AuthStore.user.id && el.type === 'CO_INSTRUCTOR') > -1 ? true : false;
  }

  get isCourseTeacher() {
    return this.$store.state.ClassesStore.classTeachers.findIndex(el => el.person.id === this.$store.state.AuthStore.user.id && el.type === 'COURSE_TEACHER') > -1 ? true : false;
  }

  get canInviteStudents() {
    return this.writeClassPriv || this.isMainTeacher || (this.classItem.allCoursesAccess ? this.isCoinstructor : (this.isCoinstructor || this.isCourseTeacher))
  }

  get itemsPerPage(): number {
    return this.pagination['itemsPerPage'];
  }

  get paginationPage(): number {
    return this.inviteStudent ? this.paginationOrg['current'] : this.pagination['page'];
  }

  get paginationPageIndex(): number {
    return this.paginationPage - 1;
  }

  get items(): any[] {
    return this.inviteStudent ? this.studentsOrg : this.classStudents;
  }

  get paginationLength(): number {

    if (this.inviteStudent && this.search.value === '') {
      return this.paginationOrg['total'];
    }

    const totalItems: number = this.items?.length ?? 0;
    return (totalItems % this.itemsPerPage === 0)
      ? totalItems / this.itemsPerPage
      : Math.floor((totalItems / this.itemsPerPage) + 1);
  }

  get pagedItems(): any[] {

    if (this.inviteStudent && this.search.value === '') {
      return this.items;
    }

    const start: number = (this.paginationPageIndex) * this.itemsPerPage;
    const end: number = start + this.itemsPerPage;

    return this.items?.slice(start, end);
  }

  get isCalendarExpired() {
    return Moment(this.classItem.schoolDay?.endDate).isBefore(Moment());
  }

  /* Methods */
  deleteItem (item: Object) {
    this.$EdModal.createConfirmationModal(
      {
        body: this.$t('classes.students.confirmRemove', {studentName: item['firstName'], className: this.classItem.name}), 
        action: async () => {
          await this.deleteItemConfirm(item);
        }
      }
    );
  }

  deleteItemConfirm (item) {
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.REMOVE_STUDENT_FROM_CLASS_A}`, 
    { classId: this.classItem.id,
    studentId: item['personId'] || item['id'] }
    ).then(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.success.studentRemoved')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.errors.studentNoRemoved')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }).finally(() => {
      this.dataTable?.updateItem(item);
    });
  }

  addStudentToClass(student: Object) {
    this.loadingAdd = true;
    this.addObj = student;
    this.dataTable?.updateItem(student);
    
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.ADD_STUDENT_TO_CLASS_A}`, 
      { classId: this.classItem.id,
      studentId: student['personId']}
    ).then(() => {
      this.loadingAdd = false;
      this.addObj = {};
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.success.studentAdded')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.errors.studentNoAdded')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }).finally(() => {
      this.dataTable?.updateItem(student);
    });
  }

  getClassStudents() {
    this.loading = true;
    
    // Get All Class Students
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_CLASS_STUDENTS_A}`,{
      classId: this.classItem.id
    }).then(() => {
      this.loading = false;
    }).catch(() => {
      // handle error
      this.loading = false;
    })
  }

  getOrganizationStudents() {
    if(this.isCalendarExpired)
      return

    this.loading = true;
    this.inviteStudent = true;

    // Check with store if the page was loaded previously    
    this.studentsOrg = this.pagedOrganizationStudents.find(el => el.page === this.paginationOrg['current']-1)?.content;

    // Fetch new page
    if (!this.studentsOrg) {
      this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_ORGANIZATION_STUDENTS_A}`,{
        orgId: this.orgId, page: this.paginationPageIndex, pageSize: this.itemsPerPage, orderByClassId:  this.classItem.id
      }).then((res) => { 
        this.studentsOrg = res.content;
        this.paginationOrg['total'] = res.totalPages;
        this.loading = false;
      }).catch(() => {
        // Handle error
        this.studentsOrg = [];
        this.inviteStudent = false;
        this.loading = false;
      })
    } else this.loading = false;
  }

  onPageChange(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;

    if (!this.inviteStudent) {
      this.pagination['page'] = detail.page;
      return;
    }

    this.paginationOrg['current'] = detail.page;

    if (this.search.value === '') {
      this.getOrganizationStudents();
      return;
    }
  }

  getClassTeachers() {
    this.loading = true;
    
    // Get All Class Teachers
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_CLASS_TEACHERS_A}`,{
      classId: this.classItem.id
    })
  }

  public refreshDataTable(): void {
    this._loadDataTable();
  }

  private _loadDataTable(): void {
    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.pagedItems || this.pagedItems?.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.username': {
        template: '{item.username}',
        templateParams: [{ key: 'item.username', value: (params) => (params.item.username || params.item.userId) ?? '' }],
      },
      'item.actions': {
        template: document.querySelector('#item\\.actions').innerHTML,
        expressions: [
          {
            target: '.spinner', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.loadingAdd && (params.parent.addObj.personId === params.item.personId),
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.action-buttons', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.loadingAdd,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.added-student', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.classStudents.find(el => el.id === params.item.id || el.id === params.item.personId),
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.add-student', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return params.parent.inviteStudent
                && params.parent.classStudents.findIndex(el => el.id === params.item.personId) === -1
                && (params.parent.addObj.personId !== params.item.personId);
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.remove-student', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return params.parent.classStudents.find(el => el.id === params.item.id || el.id === params.item.personId)
                && params.parent.canInviteStudents;
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.remove-student', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) => params.parent.isCalendarExpired,
            applied: 'ed-icon-disabled ', fallback: ' '
          },
        ],
        events: [
          { target: '.add-student', handler: (params) => params.parent.addStudentToClass(params.item) },
          { target: '.remove-student', handler: (params) => params.parent.deleteItem(params.item) },
        ]
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.pagedItems,
      headers: this.headers,
      slots: slots,
    });
  }

  private _scheduleLoadDataTable(): void {
    this.$nextTick(() => {
      this._loadDataTable();
    });
  }

  private _onLanguageChanged(lang: string): void {
    this.dataTable?.setAttribute('lang', lang);
    this._scheduleLoadDataTable();
  }

  /* Hooks */
  created() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
    this.getClassTeachers();
  }

  beforeDestroy() {
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }

  /* Watch */
  @Watch('classItem', { immediate: true })
  getStudentsByClass() {
    this.inviteStudent = false;
    this.search.value = '';
    this.$store.commit(CLASSES_STORE.MUTATIONS.CLEAR_STUDENTS_M);
    this.getClassStudents();
  }

  @Watch('classStudents', { deep: true, immediate: true })
  private _watchClassStudents(): void {
    // Needed because of last added item goes first
    this.pagination['page'] = 1;
  }

  @Watch('pagedItems', { deep: true, immediate: true })
  private _watchPagedItems(): void {
    this._scheduleLoadDataTable();
  }

  @Watch('paginationPage', { deep: true, immediate: true })
  private _watchPaginationPage(): void {
    this._scheduleLoadDataTable();
  }

  @Watch('paginationLength', { deep: true, immediate: true })
  private _watchPaginationLength(): void {
    // Refresh the pagination component from initial state
    if (this.paginationLength === 0) {
      this.showPaginator = false;
      this.$nextTick(() => {
        this.showPaginator = true;
      });
    }
  }

  @Watch('search.value', { immediate: true, deep: true })
  private _watchSearch(value): void {
    if (!this.loading || value == null || value === '') {
      this.searchStudents();
    }
  }

  searchStudents() {
    this.paginationOrg['current'] = 1;
    if (this.search.value == '') {
      this.studentsOrg = this.pagedOrganizationStudents.find(el => el.page === 0)?.content;
    } else {
      this.loading = true;
      this.$store.dispatch(`${CLASSES_STORE.ACTIONS.SEARCH_USERS_ORGANIZATION_A}`,{
        orgId: this.orgId,
        userTypes: 'STUDENT',
        keyword: this.search.value
      }).then((res) => {
        this.studentsOrg = res.content
      }).catch(() => {
        this.studentsOrg = []
      }).finally(() => {
        this.loading = false;
      });
    }
  }
}